
import { Component, Vue } from 'vue-property-decorator'
import {
  locationDataArray,
  locationGalleryItems,
  isGalleryVideo,
  galleryImageSrc,
  galleryImagePosition,
  GalleryImage,
  GalleryItem
} from '../data/loc-data'
import ScrollHintArrowsHoriz from '../components/ScrollHintArrowsHoriz.vue'

// Same breakpoint as the mobile layout (max-width: 991px), decided once at
// load: in the mobile layout the gallery is portrait, so use the portrait
// encode. No swapping on resize.
const useMobileLayout = window.matchMedia('(max-width: 991px)').matches

@Component({
  name: 'LocationView',
  components: { ScrollHintArrowsHoriz }
})
export default class LocationView extends Vue {
  private locationData = locationDataArray[0]
  private galleryItems: GalleryItem[] = locationGalleryItems.map(item =>
    (useMobileLayout && isGalleryVideo(item) && item.portrait)
      ? { ...item, ...item.portrait }
      : item
  )
  // Video items start "loaded" so the contact overlay never waits on a video
  // that is slow, blocked, or refused autoplay. Images still gate on @load.
  private imagesLoaded: boolean[] = locationGalleryItems.map(item => isGalleryVideo(item))
  // Videos fade in on 'playing' (frames actually rendering), not on load
  private videosPlaying: boolean[] = locationGalleryItems.map(() => false)
  private galleryWrapper: HTMLElement | null = null
  private videoObserver: IntersectionObserver | null = null
  // Mobile layout: the contact card waits for the video to start, then fades
  // in; desktop shows it straight away as before. Revealed once, never re-hidden.
  private overlayRevealed = !useMobileLayout
  private overlayFallbackTimer: number | null = null
  private overlayPlayingTimer: number | null = null

  isVideo(item: GalleryItem) {
    return isGalleryVideo(item)
  }

  imageSrc(item: string | GalleryImage) {
    return galleryImageSrc(item)
  }

  imagePosition(item: string | GalleryImage) {
    return galleryImagePosition(item, useMobileLayout)
  }

  get firstImageLoaded() {
    return this.imagesLoaded.length > 0 && this.imagesLoaded[0]
  }

  mounted() {
    this.galleryWrapper = this.$refs.galleryWrapper as HTMLElement

    if (this.galleryWrapper) {
      // Reset scroll to start
      this.galleryWrapper.scrollLeft = 0

      // Disable default scroll wheel behavior
      this.galleryWrapper.addEventListener('wheel', this.onWheel, { passive: false })
    }

    // Vue 2 can apply the `muted` attribute too late for the browser autoplay
    // policy, so set the property directly. The video preloads while the hero
    // is on screen, plays while it is in view and pauses when it leaves, so
    // the loop never decodes off-screen (works for both fullpage transforms
    // on desktop and normal scroll on mobile).
    const videos = Array.from(this.$el.querySelectorAll('video')) as HTMLVideoElement[]
    videos.forEach(v => { v.muted = true })
    if (videos.length == 0) {
      this.revealOverlay()
      return
    }

    this.videoObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const v = entry.target as HTMLVideoElement
        if (!entry.isIntersecting) {
          v.pause()
          return
        }
        // Safety net: never let the contact details wait on the video
        // (autoplay refused, slow network) for more than 2.5s.
        if (!this.overlayRevealed && this.overlayFallbackTimer === null) {
          this.overlayFallbackTimer = window.setTimeout(() => this.revealOverlay(), 2500)
        }
        // First play only: start on the poster's frame so the fade-in lands on
        // matching picture rather than the clip's fade-from-black.
        const posterTime = Number(v.dataset.posterTime)
        if (v.played.length == 0 && posterTime > 0) { v.currentTime = posterTime }
        const playing = v.play()
        if (playing) { playing.catch(() => { /* play refused - poster stays */ }) }
      })
    }, { threshold: 0.5 })
    videos.forEach(v => this.videoObserver?.observe(v))
  }

  destroyed() {
    if (this.galleryWrapper) {
      this.galleryWrapper.removeEventListener('wheel', this.onWheel)
    }
    this.disconnectVideoObserver()
    this.clearOverlayTimers()
  }

  private revealOverlay() {
    this.clearOverlayTimers()
    this.overlayRevealed = true
  }

  private clearOverlayTimers() {
    if (this.overlayFallbackTimer !== null) { clearTimeout(this.overlayFallbackTimer) }
    if (this.overlayPlayingTimer !== null) { clearTimeout(this.overlayPlayingTimer) }
    this.overlayFallbackTimer = null
    this.overlayPlayingTimer = null
  }

  private disconnectVideoObserver() {
    if (this.videoObserver) {
      this.videoObserver.disconnect()
      this.videoObserver = null
    }
  }

  onImgLoad(index: number) {
    setTimeout(() => {
      this.$set(this.imagesLoaded, index, true)
    }, 150) // small delay for smoother fade
  }

  onVideoPlaying(index: number) {
    this.$set(this.videosPlaying, index, true)
    // Card follows the video in: 600ms after frames start rendering
    if (!this.overlayRevealed && this.overlayPlayingTimer === null) {
      this.overlayPlayingTimer = window.setTimeout(() => this.revealOverlay(), 600)
    }
  }

  private onWheel = (e: WheelEvent) => {
    // Keep a vertical wheel from scrolling the horizontal gallery. The gallery
    // is driven by swipe and by the ScrollHintArrowsHoriz buttons only.
    e.preventDefault()
  }

}
